// src/data/verbTasks.js
// Was das Verbtraining in einer Woche abfragt: die neuen Verben der Woche im Präsens und,
// sobald weitere Zeitformen freigeschaltet sind (tenseSchedule.js), bekannte Verben aus
// früheren Wochen in diesen Zeitformen – die häufigsten zuerst.
//
// Die Auswahl ergibt sich fest aus den Wochensets (keine eigene Speicherung): Für jede
// Woche seit dem Start einer Zeitform wird nachgespielt, welche Verben schon dran waren.
import { getAllowedTenses, getTenseSchedule, getTenseStartWeek } from "./tenseSchedule";
import { PERSON_SLOTS } from "./languages";

// Zeitformen-Schlüssel (tenseSchedule.js) sind zugleich die Ordner in public/data/conjugations/.

// Welche Zeitform der Muttersprache als Übersetzung angezeigt wird (je Muttersprache).
// Indefinido und Imperfecto (ebenso das Imparfait) teilen sich das Präteritum, der Hinweis im
// Abzeichen trennt sie.
export const NATIVE_TENSE_DIRS = {
  de: {
    present: "present",
    perfect: "perfect",
    preterite: "preterite",
    imperfect: "preterite",
    future: "future",
    imperative: "imperative",
    conditional: "conditional",
    subjunctive_present: "present",
    subjunctive_imperfect: "conditional",
  },
};

// Anzeige im Abzeichen über dem Verb, je Lernsprache
export const TENSE_INFO = {
  es: {
    present: { label: "Präsens" },
    perfect: { label: "Perfecto" },
    preterite: { label: "Indefinido", hint: "abgeschlossen, einmalig" },
    imperfect: { label: "Imperfecto", hint: "Gewohnheit, Beschreibung" },
    future: { label: "Futuro" },
    imperative: { label: "Imperativo" },
    conditional: { label: "Condicional" },
    subjunctive_present: { label: "Subjuntivo Presente", hint: "nach que …" },
    subjunctive_imperfect: { label: "Subjuntivo Imperfecto", hint: "nach si / ojalá …" },
  },
  fr: {
    present: { label: "Présent" },
    perfect: { label: "Passé composé" },
    imperfect: { label: "Imparfait", hint: "Gewohnheit, Beschreibung" },
    future: { label: "Futur simple" },
    imperative: { label: "Impératif" },
    conditional: { label: "Conditionnel" },
    subjunctive_present: { label: "Subjonctif", hint: "nach que …" },
  },
};

/** Abzeichen einer Zeitform in der Lernsprache `lang` (Rückfall: Spanisch, dann Präsens) */
export function getTenseInfo(lang, tense) {
  const info = TENSE_INFO[lang] || TENSE_INFO.es;
  return info[tense] || info.present;
}

// Imperativ: keine 1. Person Singular, 3s/3p = Höflichkeitsform
export const IMPERATIVE_PRONOUNS = {
  es: { "2s": "tú", "3s": "usted", "1p": "nosotros", "2p": "vosotros", "3p": "ustedes" },
  de: { "2s": "du", "3s": "Sie", "1p": "wir", "2p": "ihr", "3p": "Sie (mehrere)" },
  fr: { "2s": "tu", "1p": "nous", "2p": "vous" },
};

// Verben, deren Imperativ nicht geübt wird: Befehl ergibt keinen Sinn (gustar, llover, nacer),
// Modal-/Hilfsverben oder Gewalt (matar). Concept-IDs, Liste wächst bei neuen Verben.
export const IMPERATIVE_EXCLUDED = new Set([
  "es-v-0004", // haber
  "es-v-0006", // saber
  "es-v-0007", // poder
  "es-v-0008", // querer
  "es-v-0014", // parecer
  "es-v-0020", // gustar
  "es-v-0022", // deber
  "es-v-0023", // importar
  "es-v-0029", // valer
  "es-v-0035", // significar
  "es-v-0037", // morir
  "es-v-0041", // matar
  "es-v-0049", // bastar
  "es-v-0050", // preocupar
  "es-v-0056", // suceder
  "es-v-0058", // faltar
  "es-v-0062", // odiar
  "es-v-0064", // lamentar
  "es-v-0070", // sonar
  "es-v-0071", // ocurrir
  "es-v-0075", // funcionar
  "es-v-0078", // encantar
  "es-v-0080", // temer
  "es-v-0090", // existir
  "es-v-0110", // pesar
  "es-v-0131", // robar
  "es-v-0184", // desaparecer
  "es-v-0189", // crecer
  "es-v-0197", // sufrir
  "es-v-0210", // aparecer
  "es-v-0224", // explotar
  "es-v-0265", // asesinar
  "es-v-0294", // durar
  "es-v-0307", // nacer
  "es-v-0333", // lastimar
  "es-v-0345", // herir
  "es-v-0348", // tardar
  "es-v-0365", // estallar
  "es-v-0373", // doler
  "es-v-0381", // llover
  "es-v-0395", // costar
  "es-v-0415", // resultar
  "es-v-0419", // enterar
  "es-v-0420", // mudar
  "es-v-0458", // encajar
  "es-v-0460", // variar
  "es-v-0463", // brillar
  "es-v-0478", // afectar
  "es-v-0483", // secuestrar
  "es-v-0507", // soler
  "es-v-0512", // merecer
  "es-v-0519", // depender
  "es-v-0520", // pertenecer
  "es-v-0522", // amenazar
  "es-v-0526", // enfermar
  "es-v-0545", // requerir
  "es-v-0559", // ahogarse
  "es-v-0560", // sangrar
  "es-v-0564", // surgir
  "es-v-0565", // apetecer
  "es-v-0566", // coincidir
  "es-v-0571", // chocar
  "es-v-0576", // cansar
  "es-v-0583", // temblar
  "es-v-0602", // convenir
  "es-v-0603", // corresponder
  "es-v-0609", // apestar
  "es-v-0610", // desmayarse
  "es-v-0628", // parecerse
  "es-v-0632", // madurar
  "es-v-0642", // implicar
  "es-v-0647", // fracasar
  "es-v-0648", // invadir
  "es-v-0649", // flotar
  "es-v-0666", // provenir
  "es-v-0668", // detestar
  "es-v-0670", // basarse
  "es-v-0689", // humillar
  "es-v-0690", // dar a luz
  "es-v-0696", // derretirse
  "es-v-0699", // envejecer
  "es-v-0702", // hacer falta
  "es-v-0703", // espérer (hoffen)
]);

// Zusätzlich je Lernsprache: Befehl nur in dieser Sprache sinnlos (Wendung oder andere Bedeutung)
export const IMPERATIVE_EXCLUDED_BY_LANG = {
  fr: new Set([
    "es-v-0019", // avoir besoin de
    "es-v-0201", // jouir de
    "es-v-0340", // manquer (à quelqu'un)
    "es-v-0429", // avoir l'intention de
    "es-v-0486", // empirer
    "es-v-0611", // tomber amoureux
  ]),
};

/** Wird der Imperativ dieses Verbs in der Lernsprache `lang` nicht geübt? */
export function isImperativeExcluded(verbId, lang) {
  return IMPERATIVE_EXCLUDED.has(verbId) || !!IMPERATIVE_EXCLUDED_BY_LANG[lang]?.has(verbId);
}

/** Ohne Akzente (é → e, ç → c), ´ als eigenes Zeichen weg */
export function stripAccents(str) {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/´/g, "");
}

// Hilfsverb être im passé composé (auch reflexiv: "t'es", "s'est")
const FRENCH_ETRE_FORMS = new Set(["suis", "es", "est", "sommes", "êtes", "sont"]);

/**
 * Richtige Antworten für eine Person: die Form aus den Konjugationsdateien, im Französischen
 * beim passé composé mit être auch die weiblichen Partizipien ("suis allée", "sommes allées";
 * "vous êtes" auch als Höflichkeitsform: "êtes allé", "êtes allée"). Die erste ist die Musterlösung.
 */
export function acceptedVerbForms(forms, slot, tense, lang) {
  const expected = (forms?.[slot] || "").trim();
  if (!expected) return [];
  if (lang !== "fr" || tense !== "perfect") return [expected];

  const words = expected.split(" ");
  const aux = (words[words.length - 2] || "").replace(/^[mts]'/, "");
  if (words.length < 2 || !FRENCH_ETRE_FORMS.has(aux)) return [expected];

  // männliches Partizip im Singular aus der 1. bzw. 3. Person
  const singular = ((forms["1s"] || forms["3s"] || "").trim().split(" ").pop()) || "";
  const prefix = words.slice(0, -1).join(" ");
  const variants = slot.endsWith("s")
    ? [singular + "e"]
    : [singular + "es", ...(slot === "2p" ? [singular, singular + "e"] : [])];
  return [...new Set([expected, ...variants.map((p) => `${prefix} ${p}`)])];
}

/**
 * Bewertet eine eingetippte Verbform gegen die richtigen Antworten (`candidates`, siehe
 * acceptedVerbForms). Groß-/Kleinschreibung egal, ’ = ', fehlende Akzente = "accent".
 * Französisch: das angezeigte Subjektpronomen darf mit eingetippt werden ("j'ai", "il mange").
 * Rückgabe: { status: "correct" | "accent" | "wrong" | "empty", expected, given }
 */
export function gradeVerbAnswer(givenRaw, candidates, { lang, pronoun } = {}) {
  const expected = candidates[0] || "";
  const given = (givenRaw || "").trim();
  if (!expected && !given) return { status: "empty", expected, given };
  if (!expected || !given) return { status: "wrong", expected, given };

  const lower = (s) => s.toLowerCase().replace(/[’‘]/g, "'");
  // mit und ohne vorn abgeschnittenes Pronomen vergleichen: bei reflexiven Verben gehört
  // "nous"/"vous" zur Form selbst ("nous souvenons" = nous nous souvenons)
  const answers = [lower(given)];
  if (lang === "fr" && pronoun) {
    for (const p of pronoun.split("/").map(lower)) {
      if (answers[0].startsWith(p.endsWith("'") ? p : p + " ")) {
        answers.push(answers[0].slice(p.length).trim());
        break;
      }
    }
  }

  let status = "wrong";
  for (const candidate of candidates) {
    const c = lower(candidate);
    for (const answer of answers) {
      if (c === answer) return { status: "correct", expected, given };
      if (stripAccents(c) === stripAccents(answer)) status = "accent";
    }
  }
  return { status, expected, given };
}

// Aufgaben in anderen Zeitformen pro Woche, je Tempo
export const TENSE_TASKS_PER_WEEK = { hard: 6, medium: 5, easy: 4 };
// So viele Wochen nach ihrem Start bekommt eine neue Zeitform alle Aufgaben
export const INTRO_WEEKS = 4;

/** Schlüssel einer Aufgabe = verb_id in verb_progress ("es-v-0005", "es-v-0005@preterite") */
export function taskKey(verbId, tense) {
  return tense === "present" ? verbId : `${verbId}@${tense}`;
}

/** Personen, die in einer Zeitform abgefragt werden (ohne leere Felder und "-") */
export function slotsForForms(forms, tense) {
  return PERSON_SLOTS.filter((slot) => {
    if (tense === "imperative" && slot === "1s") return false;
    const form = (forms?.[slot] || "").trim();
    return form !== "" && form !== "-";
  });
}

function rankOf(verbId) {
  const n = parseInt(String(verbId).split("-").pop(), 10);
  return Number.isNaN(n) ? Infinity : n;
}

/**
 * Welche Zeitformen in Woche `week` die Aufgaben bekommen (Länge = Aufgaben pro Woche).
 * In den ersten INTRO_WEEKS Wochen nach dem Start der neuesten Zeitform nur diese, sonst reihum alle.
 */
export function tenseSlotsForWeek(week, pace, lang) {
  const allowed = getAllowedTenses(week, pace, lang);
  const unlocked = getTenseSchedule(lang).map((t) => t.tense).filter(
    (t) => t !== "present" && allowed[t]
  );
  if (unlocked.length === 0) return [];

  const count = TENSE_TASKS_PER_WEEK[pace] ?? TENSE_TASKS_PER_WEEK.medium;
  // nur die zuletzt gestartete(n) Zeitform(en), z. B. Futuro + Imperativo gemeinsam
  const newestStart = Math.max(...unlocked.map((t) => getTenseStartWeek(t, pace, lang)));
  const intro =
    week - newestStart < INTRO_WEEKS
      ? unlocked.filter((t) => getTenseStartWeek(t, pace, lang) === newestStart)
      : [];
  const pool = intro.length > 0 ? intro : unlocked;
  const offset = intro.length > 0 ? 0 : week * count;
  return Array.from({ length: count }, (_, i) => pool[(offset + i) % pool.length]);
}

/**
 * Aufgaben der Woche `weekIndex`: [{ key, verbId, tense }], zuerst die Präsens-Aufgaben.
 * - newVerbIds: Verben des aktuellen Wochensets
 * - learnedByWeek: { [woche]: [verbIds] } der früheren Wochen
 * - isEligible(verbId, tense): gibt es Formen in beiden Sprachen, Imperativ sinnvoll?
 * - lang: Lernsprache (Zeitplan aus tenseSchedule.js, ohne Angabe Spanisch)
 */
export function buildVerbTasks({ weekIndex, pace, newVerbIds = [], learnedByWeek = {}, isEligible = () => true, lang }) {
  const tasks = newVerbIds
    .filter((id) => isEligible(id, "present"))
    .map((verbId) => ({ key: taskKey(verbId, "present"), verbId, tense: "present" }));

  const covered = {}; // Zeitform → Set der schon geübten Verben
  const learned = new Set();
  for (let w = 2; w <= (weekIndex || 1); w++) {
    (learnedByWeek[w - 1] || []).forEach((id) => learned.add(id));
    const sorted = [...learned].sort((a, b) => rankOf(a) - rankOf(b));
    const pickedThisWeek = {};

    for (const tense of tenseSlotsForWeek(w, pace, lang)) {
      covered[tense] = covered[tense] || new Set();
      pickedThisWeek[tense] = pickedThisWeek[tense] || new Set();
      const candidates = sorted.filter(
        (id) => isEligible(id, tense) && !pickedThisWeek[tense].has(id)
      );
      if (candidates.length === 0) continue;
      let verbId = candidates.find((id) => !covered[tense].has(id));
      if (!verbId) {
        // alle bekannten Verben in dieser Zeitform schon dran gewesen: von vorn beginnen
        covered[tense] = new Set(pickedThisWeek[tense]);
        verbId = candidates[0];
      }
      covered[tense].add(verbId);
      pickedThisWeek[tense].add(verbId);
      if (w === weekIndex) tasks.push({ key: taskKey(verbId, tense), verbId, tense });
    }
  }
  return tasks;
}
